import { Ministration } from "@interfaces/catalogs";
import dayjs from "dayjs";

const DEFAULT_INTEREST_RATE = 17.5;

export type MaturityCollectionResult = {
  days: number;
  interest: number;
  total: number;
};

/**
 * Cobro al vencimiento con interés simple y base comercial de 360 días.
 * interés = capital × (tasa%/100) × (días / 360)
 * total   = capital + interés
 */
export const calculateMaturityCollection = (
  capital: number,
  maturityDate: string,
  annualRate: number = DEFAULT_INTEREST_RATE,
  startDate?: string,
): MaturityCollectionResult => {
  const start = dayjs(startDate).startOf("day");
  const maturity = dayjs(maturityDate).startOf("day");

  if (!start.isValid() || !maturity.isValid() || capital <= 0) {
    return { days: 0, interest: 0, total: Number(Math.max(capital, 0).toFixed(2)) };
  }

  const days = Math.max(maturity.diff(start, "day"), 0);
  const rate = annualRate > 1 ? annualRate / 100 : annualRate;
  const interest = Number((capital * rate * (days / 360)).toFixed(2));
  const total = Number((capital + interest).toFixed(2));

  return { days, interest, total };
};

export type MinistrationScheduleItem = {
  order: number;
  credit: number;
};

/** Total por ministración: credit (por ha) × hectáreas. */
export const calculateMinistrationSchedule = (
  ministrations: Ministration[] | null | undefined,
  hectares: number,
): MinistrationScheduleItem[] => {
  if (!ministrations?.length || hectares <= 0) {
    return [];
  }

  return ministrations.map((ministration) => ({
    order: ministration.order,
    credit: ministration.credit * hectares,
  }));
};

/** Plazo en meses entre la fecha de la primera y la última ministración. */
export const calculateLoanTermMonths = (ministrations: Ministration[] | null | undefined): number => {
  if (!ministrations?.length) {
    return 0;
  }

  const sortedDates = ministrations
    .map((ministration) => dayjs(ministration.date))
    .filter((date) => date.isValid())
    .sort((firstDate, secondDate) => firstDate.valueOf() - secondDate.valueOf());

  if (sortedDates.length < 2) {
    return 0;
  }

  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];

  return Math.max(lastDate.diff(firstDate, "month"), 0);
};

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// convierte "2027-01-31T00:00:00" -> "31 ene 2027"
export const formatMaturityDate = (isoDate: string) => {
  if (!isoDate) return "";

  const [year, month, day] = isoDate.split("T")[0].split("-").map(Number);

  return `${day} ${MONTHS[month - 1]} ${year}`;
};
